/**
 * Checks candidate shader code in the real runtime before it replaces what a
 * node is running — the same sandboxed document the node previews with, in a
 * hidden iframe of its own, so a visible shader is never touched.
 *
 * The bridge says `genie:ready` before the runtime library and the user's code
 * run, so ready proves nothing. A candidate passes once its document has fully
 * loaded (`genie:loaded`) and then runs for a while without reporting an error.
 * That time only counts while the page is visible: a hidden page runs no
 * animation frames, so a draw loop that throws would never get the chance to.
 */

import { IframeManager } from './iframe-manager'
import type { ShaderValidationResult, ShaderValidator } from './authoring/types'
import type { RuntimeMode } from './types'

export interface ShaderValidationCandidate {
  code: string
  runtimeMode: RuntimeMode
  /** Cancels the check: the sandbox is torn down and the next one in line can start. */
  signal?: AbortSignal
}

export interface ShaderValidationOptions {
  /** Error-free running time after the document loads. */
  settleMs?: number
  /** Give up after this long; CDN runtimes (p5, three.js) can be slow to arrive. */
  timeoutMs?: number
  width?: number
  height?: number
}

const DEFAULTS: Required<ShaderValidationOptions> = { settleMs: 1500, timeoutMs: 10_000, width: 256, height: 144 }

// One at a time: every candidate holds a WebGL context, and browsers drop the oldest
// context (a visible shader's) when a page opens too many.
let queue: Promise<unknown> = Promise.resolve()

const ABORTED: ShaderValidationResult = { ok: false, kind: 'aborted', message: 'The check was cancelled.' }

/** Run candidate code in a hidden copy of the runtime and report the first error, if any. */
export const validateShader: ShaderValidator = (candidate: ShaderValidationCandidate) => runShaderValidation(candidate)

export function runShaderValidation(
  candidate: ShaderValidationCandidate,
  options: ShaderValidationOptions = {},
): Promise<ShaderValidationResult> {
  const run = queue.then(() => validateOnce(candidate, { ...DEFAULTS, ...options }))
  queue = run.catch(() => undefined)
  return run
}

/** A timer that only runs while the page is visible, picking up where it left off. */
function visibleTimer(ms: number, onDone: () => void): { cancel: () => void } {
  let remaining = ms
  let startedAt = 0
  let timer: ReturnType<typeof setTimeout> | undefined
  const visible = () => document.visibilityState === 'visible'
  const run = () => {
    startedAt = Date.now()
    timer = setTimeout(done, remaining)
  }
  const done = () => {
    document.removeEventListener('visibilitychange', onVisibility)
    onDone()
  }
  const onVisibility = () => {
    if (visible() && timer === undefined) run()
    else if (!visible() && timer !== undefined) {
      clearTimeout(timer)
      timer = undefined
      remaining = Math.max(0, remaining - (Date.now() - startedAt))
    }
  }
  document.addEventListener('visibilitychange', onVisibility)
  if (visible()) run()
  return {
    cancel: () => {
      clearTimeout(timer)
      document.removeEventListener('visibilitychange', onVisibility)
    },
  }
}

function validateOnce(
  { code, runtimeMode, signal }: ShaderValidationCandidate,
  { settleMs, timeoutMs, width, height }: Required<ShaderValidationOptions>,
): Promise<ShaderValidationResult> {
  // Cancelled while it waited in line: never start a sandbox.
  if (signal?.aborted) return Promise.resolve(ABORTED)
  if (!code.trim()) return Promise.resolve({ ok: false, message: 'The shader is empty.' })

  // Browsers stop animation frames in hidden and off-screen frames, and a sketch's draw loop
  // must run to be checked: keep the sandbox in the viewport, a few transparent pixels wide.
  const host = document.createElement('div')
  host.setAttribute('aria-hidden', 'true')
  Object.assign(host.style, { position: 'fixed', right: '0', bottom: '0', width: '4px', height: '4px', opacity: '0', pointerEvents: 'none', overflow: 'hidden', zIndex: '-1' })
  document.body.appendChild(host)
  const manager = new IframeManager({ width, height })

  return new Promise<ShaderValidationResult>((resolve) => {
    let settled = false
    let settleTimer: { cancel: () => void } | undefined
    const onAbort = () => finish(ABORTED)
    const finish = (result: ShaderValidationResult) => {
      if (settled) return
      settled = true
      timeout.cancel()
      settleTimer?.cancel()
      signal?.removeEventListener('abort', onAbort)
      manager.destroy()
      host.remove()
      resolve(result)
    }
    // Both clocks stop while the page is hidden, where the sandbox can neither load promptly nor draw.
    const timeout = visibleTimer(timeoutMs, () => {
      finish({ ok: false, kind: 'timeout', message: `The shader did not finish starting within ${Math.round(timeoutMs / 1000)} seconds.` })
    })
    signal?.addEventListener('abort', onAbort)

    manager.setCallbacks({
      onLoaded: () => {
        settleTimer?.cancel()
        settleTimer = visibleTimer(settleMs, () => finish({ ok: true }))
      },
      onError: (error) => {
        finish({ ok: false, message: error.message || 'The shader failed to run.', line: error.line, kind: error.kind })
      },
    })
    manager.create(host)
    manager.loadCode(code, runtimeMode)
  })
}
